import {
  Root,
  Trigger,
  Portal,
  Content,
  Item,
  Separator,
  Label,
} from "@radix-ui/react-dropdown-menu";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { cn } from "../../lib/cn";

function DropdownContent({
  className,
  align = "end",
  sideOffset = 6,
  children,
}: ComponentPropsWithoutRef<typeof Content>) {
  return (
    <Portal>
      <Content
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 min-w-44 rounded-md border border-neutral-800 bg-neutral-900 p-1 shadow-md",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-90 data-[state=closed]:fade-out",
          className,
        )}
      >
        {children}
      </Content>
    </Portal>
  );
}

function DropdownItem({
  className,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof Item>) {
  return (
    <Item
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-neutral-200",
        "outline-none data-[highlighted]:bg-neutral-800 data-[highlighted]:text-neutral-50",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...rest}
    >
      {children}
    </Item>
  );
}

function DropdownLabel({ children }: { children: ReactNode }) {
  return (
    <Label className="px-2 py-1 text-[10px] uppercase tracking-wider text-neutral-500">
      {children}
    </Label>
  );
}

function DropdownSeparator() {
  return <Separator className="my-1 h-px bg-neutral-800" />;
}

export const DropdownMenu = Object.assign(Root, {
  Trigger,
  Content: DropdownContent,
  Item: DropdownItem,
  Label: DropdownLabel,
  Separator: DropdownSeparator,
});
